import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import { useTenantContext } from '../../auth/useTenantContext';
import { Drawer } from '../ui/Drawer';
import { VpSelect } from '../ui/VpSelect';
import { useTenantAdminWarehouses } from '../../api/adminWarehouses';
import { useCreateAdminUser, type Role } from '../../api/adminUsers';
import { PasswordGenerator } from '../tenants/PasswordGenerator';

interface Props { isOpen: boolean; onClose: () => void; inline?: boolean }

export function NewUserDrawer({ isOpen, onClose, inline }: Props) {
  const { t } = useTranslation(['user', 'common']);
  const { token, tenantCode } = useTenantContext();
  const { data: warehouses } = useTenantAdminWarehouses(token, tenantCode);
  const createMut = useCreateAdminUser(token, tenantCode);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('tenant-admin');
  const [warehouseId, setWarehouseId] = useState<number | null>(null);
  const [conflict, setConflict] = useState(false);
  const [conflictKcSub, setConflictKcSub] = useState<string | null>(null);
  const [invariantError, setInvariantError] = useState<string | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [idemKey] = useState(() => crypto.randomUUID());

  const submit = async (acceptExisting: boolean) => {
    setConflict(false);
    setInvariantError(null);
    setGenericError(null);
    if (role !== 'tenant-admin' && warehouseId === null) {
      setGenericError(t('user:newUser.errorWarehouseRequired', { role }));
      return;
    }
    try {
      await createMut.mutateAsync({
        body: {
          email,
          display_name: displayName,
          password_temp: password,
          role,
          warehouse_id: role === 'tenant-admin' ? null : warehouseId,
          accept_existing: acceptExisting,
        },
        idempotencyKey: idemKey,
      });
      onClose();
    } catch (err: unknown) {
      const e = err as { body?: { code?: string; detail?: string; existing_kc_sub?: string }; message?: string };
      const c = e?.body?.code;
      if (c === 'email_exists') {
        setConflict(true);
        setConflictKcSub(e.body?.existing_kc_sub ?? null);
      } else if (c === 'voice_operator_membership_invariant') {
        setInvariantError(t('user:newUser.errorOperatorSingleMembership'));
      } else {
        setGenericError(e?.body?.detail ?? e?.message ?? t('user:memberships.errorGeneric'));
      }
    }
  };

  const onSubmit = (e: FormEvent) => { e.preventDefault(); submit(false); };

  const inner = (
      <form onSubmit={onSubmit} className="d-flex flex-column gap-2">
        <FormGroup className="mb-0">
          <Label for="nu-email" className="mb-1">{t('user:newUser.emailLabel')}</Label>
          <Input id="nu-email" aria-label={t('user:newUser.emailLabel')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </FormGroup>
        <FormGroup className="mb-0">
          <Label for="nu-display" className="mb-1">{t('user:newUser.displayNameLabel')}</Label>
          <Input id="nu-display" aria-label={t('user:newUser.displayNameLabel')} value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={128} required />
        </FormGroup>
        <FormGroup className="mb-0">
          <Label for="nu-password" className="mb-1">{t('user:newUser.passwordLabel')}</Label>
          <div className="d-flex gap-2">
            <Input id="nu-password" aria-label={t('user:newUser.passwordAria')} type="text" value={password} onChange={(e) => setPassword(e.target.value)} minLength={12} required style={{ flex: 1 }} />
            <PasswordGenerator onGenerated={setPassword} value={password} />
          </div>
        </FormGroup>
        <FormGroup tag="fieldset" className="mb-0">
          <legend className="fs-13 fw-medium text-muted mb-1">{t('user:newUser.roleLabel')}</legend>
          {(['tenant-admin', 'warehouse-admin', 'voice-supervisor', 'voice-operator'] as Role[]).map((r) => (
            <FormGroup check inline key={r}>
              <Input type="radio" name="role" id={`nu-role-${r}`} checked={role === r} onChange={() => setRole(r)} aria-label={r} />
              <Label check for={`nu-role-${r}`}>{t(`user:roles.${r}`, { defaultValue: r })}</Label>
            </FormGroup>
          ))}
        </FormGroup>
        {role !== 'tenant-admin' && (
          <label className="d-flex flex-column gap-1">
            <span className="form-label mb-0">{t('user:newUser.warehouseLabel')}</span>
            <VpSelect
              options={(warehouses?.data ?? []).map((w) => ({ value: w.id, label: `${w.code} · ${w.name}` }))}
              value={warehouseId}
              onChange={(v) => setWarehouseId(v === null ? null : Number(v))}
              placeholder={t('user:newUser.warehousePlaceholder')}
            />
          </label>
        )}

        {conflict && (
          <div role="alert" className="alert alert-warning py-2 px-3 mb-0">
            <p className="mb-2">
              {t('user:newUser.conflictBody')}{conflictKcSub ? t('user:newUser.conflictSubSuffix', { sub: conflictKcSub }) : ''}.
            </p>
            <Button type="button" color="primary" size="sm" onClick={() => submit(true)}>
              {t('user:newUser.linkOnlyAction')}
            </Button>
          </div>
        )}
        {invariantError && <p role="alert" className="text-danger small mb-0">{invariantError}</p>}
        {genericError && <p role="alert" className="text-danger small mb-0">{genericError}</p>}

        <div className="d-flex justify-content-end gap-2">
          <Button type="button" color="light" onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button type="submit" color="primary">{t('user:newUser.submit')}</Button>
        </div>
      </form>
  );
  return inline ? inner : (
    <Drawer isOpen={isOpen} onClose={onClose} title={t('user:newUser.drawerTitle')}>{inner}</Drawer>
  );
}
